// Legend building utilities for roster coloring modes

import {
    UserDateRow,
    ShiftMetadata,
    LegendItem,
    ColorContext
} from './types';

/**
 * Helper: Check if a key is a date field (not a metadata field)
 */
function isDateField(key: string): boolean {
    return key !== 'userId' && key !== 'userEmail' && key !== 'userDisplayName' &&
           key !== 'teamId' && key !== 'teamName';
}

/**
 * Helper: Extract shift metadata from a cell value
 */
function getShiftMetadata(value: unknown): ShiftMetadata | null {
    if (typeof value === 'object' && value !== null && 'displayText' in value) {
        return value as ShiftMetadata;
    }
    return null;
}

/**
 * Helper: Create legend item from matching location
 */
function createLegendFromMatchingLocation(matchingLocation: {
    id: string;
    name: string;
    label?: string;
    color?: string;
    colour?: string;
}): LegendItem {
    return {
        id: matchingLocation.id,
        name: matchingLocation.label || matchingLocation.name,
        color: matchingLocation.color || matchingLocation.colour || '#cccccc'
    };
}

/**
 * Helper: Create legend item from location metadata
 */
function createLegendFromLocationMetadata(location: { id: string; name: string; color?: string }): LegendItem {
    return {
        id: location.id,
        name: location.name,
        color: location.color || '#cccccc'
    };
}

/**
 * Helper: Process location for legend
 */
function processLocation(
    location: { id: string; name: string; color?: string },
    colorContext: ColorContext,
    legendMap: Map<string, LegendItem>
): void {
    if (!location.id || legendMap.has(location.id)) {
        return;
    }

    const matchingLocation = colorContext.locations?.find((loc) => loc.id === location.id);
    
    if (matchingLocation) {
        legendMap.set(location.id, createLegendFromMatchingLocation(matchingLocation));
        return;
    }
    
    if (location.name) {
        legendMap.set(location.id, createLegendFromLocationMetadata(location));
    }
}

/**
 * Helper: Process all date fields in a row for locations
 */
function processRowForLocations(
    row: UserDateRow,
    colorContext: ColorContext,
    legendMap: Map<string, LegendItem>
): void {
    for (const key in row) {
        if (!isDateField(key)) {
            continue;
        }
        
        const metadata = getShiftMetadata(row[key]);
        if (!metadata?.location) {
            continue;
        }
        
        processLocation(metadata.location, colorContext, legendMap);
    }
}

/**
 * Build legend for LOCATIONS mode - EXACTLY matches Angular rebuildLegendLocations()
 * Angular reference: lines 406-445 in my-team-roster.controller.js
 *
 * @param rowData - User-date grid rows containing shift metadata
 * @param colorContext - Available tasks, templates, locations for lookup
 * @returns Array of legend items with id, name, color
 */
export function rebuildLegendLocations(rowData: UserDateRow[], colorContext: ColorContext): LegendItem[] {
    const legendMap = new Map<string, LegendItem>();
    
    // Iterate through all rows and their date columns to find shifts
    for (const row of rowData) {
        processRowForLocations(row, colorContext, legendMap);
    }
    
    return Array.from(legendMap.values());
}

/**
 * Helper: Create legend item from matching template
 */
function createLegendFromMatchingTemplate(matchTemplate: {
    id: string | number;
    name: string;
    label?: string;
    color?: string;
    colour?: string;
}): LegendItem {
    return {
        id: String(matchTemplate.id),
        name: matchTemplate.label || matchTemplate.name || 'Unknown Template',
        color: matchTemplate.color || matchTemplate.colour || '#cccccc'
    };
}

/**
 * Helper: Create legend item from template metadata
 */
function createLegendFromTemplateMetadata(shiftTemplateId: string, metadata: ShiftMetadata): LegendItem {
    return {
        id: shiftTemplateId,
        name: metadata.shiftTemplateName || `Template ${shiftTemplateId.substring(0, 8)}...`,
        color: metadata.shiftTemplateColor || '#cccccc'
    };
}

/**
 * Helper: Apply color fallback from metadata if needed
 */
function applyColorFallback(legendItem: LegendItem, metadata: ShiftMetadata): void {
    if ((!legendItem.color || legendItem.color === '#cccccc') && metadata.shiftTemplateColor) {
        legendItem.color = metadata.shiftTemplateColor;
    }
}

/**
 * Helper: Find matching shift template in context
 */
function findMatchingTemplate(
    shiftTemplateId: string,
    colorContext: ColorContext
): { id: string | number; name: string; label?: string; color?: string; colour?: string } | undefined {
    return colorContext.shiftTemplates?.find(
        (st) => st.id === shiftTemplateId || String(st.id) === String(shiftTemplateId)
    );
}

/**
 * Helper: Process shift template for legend
 */
function processShiftTemplate(
    metadata: ShiftMetadata,
    colorContext: ColorContext,
    legendMap: Map<string, LegendItem>
): void {
    const shiftTemplateId = metadata.shiftTemplateId;
    
    if (!shiftTemplateId || legendMap.has(shiftTemplateId)) {
        return;
    }

    const matchTemplate = findMatchingTemplate(shiftTemplateId, colorContext);
    
    if (matchTemplate) {
        const legendItem = createLegendFromMatchingTemplate(matchTemplate);
        applyColorFallback(legendItem, metadata);
        legendMap.set(shiftTemplateId, legendItem);
        return;
    }
    
    if (metadata.shiftTemplateName || metadata.shiftTemplateColor) {
        legendMap.set(shiftTemplateId, createLegendFromTemplateMetadata(shiftTemplateId, metadata));
    }
}

/**
 * Helper: Process all date fields in a row for templates
 */
function processRowForTemplates(
    row: UserDateRow,
    colorContext: ColorContext,
    legendMap: Map<string, LegendItem>
): void {
    for (const key in row) {
        if (!isDateField(key)) {
            continue;
        }
        
        const metadata = getShiftMetadata(row[key]);
        if (!metadata) {
            continue;
        }
        
        processShiftTemplate(metadata, colorContext, legendMap);
    }
}

/**
 * Build legend for TEMPLATES mode - EXACTLY matches Angular rebuildLegendTemplates()
 * Angular reference: lines 447-466 in my-team-roster.controller.js
 *
 * @param rowData - User-date grid rows containing shift metadata
 * @param colorContext - Available tasks, templates, locations for lookup
 * @returns Array of legend items with id, name, color
 */
export function rebuildLegendTemplates(rowData: UserDateRow[], colorContext: ColorContext): LegendItem[] {
    const legendMap = new Map<string, LegendItem>();
    
    // Iterate through all rows and their date columns to find shifts
    for (const row of rowData) {
        processRowForTemplates(row, colorContext, legendMap);
    }
    
    return Array.from(legendMap.values());
}

/**
 * Main legend builder - dispatches to mode-specific function
 * Matches Angular rebuildLegend() dispatcher (lines 368-382)
 *
 * @param rowData - User-date grid rows containing shift metadata
 * @param colorContext - Contains colorMode and lookup arrays
 * @returns Array of legend items for the current mode
 */
export function rebuildLegend(rowData: UserDateRow[], colorContext: ColorContext): LegendItem[] {
    const mode = colorContext.colorMode;
    
    if (mode === 'NONE') {
        return [];
    }
    
    if (mode === 'TEMPLATES') {
        return rebuildLegendTemplates(rowData, colorContext);
    } else if (mode === 'LOCATIONS') {
        return rebuildLegendLocations(rowData, colorContext);
    }
    
    return [];
}
